/**
 * Blow Room QC Form — Layer 2.
 *
 * Per-laydown observations from the BR side: moisture, trash before /
 * after, cleaning efficiency (auto-derived), waste %, contamination,
 * tuft size, chute uniformity. Distinct from the per-sample Chute Feed
 * test — this is the lot-level go/no-go.
 *
 * Submits to /checklist-logs with entry_type=BR_QC.
 */
import { useMemo, useState } from 'react'
import {
  App, Button, Card, Col, Form, Input, InputNumber, Row, Select, Space,
  Statistic, Typography,
} from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { filterActiveShifts } from '@/shared/constants/shifts'
import { plantsApi } from '@/features/masters/plants/api'
import { shiftsApi } from '@/features/gate/api'
import { laydownApi } from '@/features/baleManagement/api'
import { checklistLogsApi } from '@/features/baleManagement/api'
import { ReadOnlyTile } from './_shared'

const { Text } = Typography

const NEP_LEVELS = ['LOW', 'MEDIUM', 'HIGH']
const CONTAMINATION_LEVELS = ['NONE', 'LOW', 'MEDIUM', 'HIGH']
const TUFT_SIZE = ['SMALL', 'NORMAL', 'LARGE', 'OVERSIZE']
const CHUTE_UNIFORMITY = ['UNIFORM', 'SLIGHT_VARIATION', 'IRREGULAR']
const QC_DECISIONS = [
  { value: 'PASS',             label: 'PASS' },
  { value: 'HOLD',             label: 'HOLD' },
  { value: 'SETTINGS_REVIEW',  label: 'SETTINGS REVIEW' },
  { value: 'REJECTED',         label: 'REJECTED' },
]

export default function BlowRoomQCPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [laydownId, setLaydownId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [trashBefore, setTrashBefore] = useState<number | null>(null)
  const [trashAfter, setTrashAfter] = useState<number | null>(null)

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const plantOptions = plantsData?.items?.map((p: any) => ({ label: p.name, value: p.id })) ?? []

  // Active laydowns to attach the BR QC observation to.
  const { data: laydowns = [] } = useQuery({
    queryKey: ['laydowns-active', plantId],
    queryFn: () => laydownApi.list({ plant_id: plantId, limit: 50 }),
    enabled: !!plantId,
  })
  const laydownOptions = (Array.isArray(laydowns) ? laydowns : [])
    .filter((l: any) => ['RELEASED', 'CONDITIONING', 'READY_FOR_MIX', 'MIXING_IN_PROGRESS', 'MIXING_COMPLETED'].includes(l.status))
    .map((l: any) => ({
      label: `${l.laydown_no} — ${l.count_description || 'no count'} [${l.status}]`,
      value: l.id,
    }))

  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts-all-active'],
    queryFn: async () => filterActiveShifts(await shiftsApi.list()),
  })

  // Cleaning efficiency formula: (before − after) / before × 100
  const cleaningEff = useMemo(() => {
    if (trashBefore == null || trashAfter == null || trashBefore <= 0) return null
    return Math.max(0, Math.min(100, ((trashBefore - trashAfter) / trashBefore) * 100))
  }, [trashBefore, trashAfter])

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => checklistLogsApi.upsert(body),
    onSuccess: () => {
      message.success('BR QC observation saved')
      qc.invalidateQueries({ queryKey: ['br-qc-recent'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })

  const onSubmit = async () => {
    const v = await form.validateFields()
    if (!plantId || !shiftId) {
      message.error('Plant + shift are required')
      return
    }
    saveMutation.mutate({
      plant_id: plantId,
      log_date: dayjs().format('YYYY-MM-DD'),
      entry_type: 'BR_QC',
      shift_id: shiftId,
      laydown_plan_id: laydownId || null,
      items_json: {
        moisture_pct: v.moisture_pct ?? null,
        trash_before_pct: trashBefore,
        trash_after_pct: trashAfter,
        cleaning_efficiency_pct: cleaningEff != null ? Number(cleaningEff.toFixed(2)) : null,
        waste_pct: v.waste_pct ?? null,
        good_fiber_in_waste_pct: v.good_fiber_in_waste_pct ?? null,
        nep_observation: v.nep_observation || null,
        contamination_level: v.contamination_level || null,
        tuft_size_observation: v.tuft_size_observation || null,
        chute_feed_uniformity: v.chute_feed_uniformity || null,
      },
      decision: v.decision || null,
      corrective_action: v.corrective_action || null,
      remarks: v.remarks || null,
    })
  }

  return (
    <div>
      <PageHeader
        title="Blow Room — QC Observations"
        subtitle="Per-laydown sign-off. Cleaning efficiency = (trash_before − trash_after) ÷ trash_before × 100 — auto."
      />
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card>
            <Form layout="vertical" form={form} size="large">
              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item label="Plant" required>
                    <Select options={plantOptions} value={plantId} onChange={setPlantId} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Shift" required>
                    <Select
                      options={(shifts || []).map((s: any) => ({
                        label: s.shift_name || s.shift_code, value: s.id,
                      }))}
                      value={shiftId} onChange={setShiftId}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Laydown">
                    <Select
                      options={laydownOptions} value={laydownId} onChange={setLaydownId}
                      placeholder={laydownOptions.length ? 'Select laydown' : 'No active laydowns'}
                      showSearch optionFilterProp="label"
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Card type="inner" title="Cotton parameters">
                <Row gutter={12}>
                  <Col xs={12} md={6}>
                    <Form.Item name="moisture_pct" label="Moisture %">
                      <InputNumber style={{ width: '100%' }} step={0.1} min={0} max={20} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Trash before %">
                      <InputNumber
                        style={{ width: '100%' }} step={0.1} min={0} max={20}
                        value={trashBefore} onChange={v => setTrashBefore(v)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Trash after %">
                      <InputNumber
                        style={{ width: '100%' }} step={0.1} min={0} max={20}
                        value={trashAfter} onChange={v => setTrashAfter(v)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <ReadOnlyTile
                      label="Cleaning efficiency"
                      value={cleaningEff != null ? `${cleaningEff.toFixed(1)}%` : '—'}
                      hint="(before − after) / before × 100"
                      source="SYSTEM"
                    />
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={12} md={6}>
                    <Form.Item name="waste_pct" label="Waste %">
                      <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item name="good_fiber_in_waste_pct" label="Good fiber in waste %">
                      <InputNumber style={{ width: '100%' }} step={0.1} min={0} max={100} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card type="inner" title="Observations" style={{ marginTop: 12 }}>
                <Row gutter={12}>
                  <Col xs={12} md={6}>
                    <Form.Item name="nep_observation" label="Nep level">
                      <Select options={NEP_LEVELS.map(v => ({ value: v, label: v }))} allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item name="contamination_level" label="Contamination">
                      <Select options={CONTAMINATION_LEVELS.map(v => ({ value: v, label: v }))} allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item name="tuft_size_observation" label="Tuft size">
                      <Select options={TUFT_SIZE.map(v => ({ value: v, label: v }))} allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item name="chute_feed_uniformity" label="Chute feed">
                      <Select options={CHUTE_UNIFORMITY.map(v => ({ value: v, label: v.replace('_', ' ') }))} allowClear />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card type="inner" title="QC decision" style={{ marginTop: 12 }}>
                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="decision" label="Decision" rules={[{ required: true }]}>
                      <Select options={QC_DECISIONS} placeholder="Pass / Hold / Settings review / Rejected" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item name="corrective_action" label="Corrective action">
                      <Input placeholder="e.g. open beater clearance 1mm" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea rows={2} maxLength={300} />
                </Form.Item>
              </Card>

              <Button
                type="primary" size="large" block icon={<SaveOutlined />} style={{ marginTop: 12 }}
                loading={saveMutation.isPending} onClick={onSubmit}
              >
                Save BR QC observation
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Why a separate BR QC?">
            <Space direction="vertical">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Chute Feed test = mid-mix HVI sample. <b>BR QC = lot-level go/no-go.</b>{' '}
                Decisions made here drive whether the laydown gets clearance to start carding.
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                If you mark <b>HOLD</b> or <b>REJECTED</b>, downstream cans should not move
                until QC reviews — coordinate with the Chute Feed flow.
              </Text>
              <Statistic
                title="Cleaning efficiency"
                value={cleaningEff != null ? cleaningEff.toFixed(1) : '—'}
                suffix={cleaningEff != null ? '%' : ''}
                valueStyle={{ color: cleaningEff && cleaningEff >= 60 ? '#52c41a' : '#faad14', fontSize: 28 }}
              />
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
